import { NextPage } from "next";
import { useKeycloak } from "@react-keycloak-fork/ssr";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { routes } from "@/routes/routes";
import HeroBlock from "@/components/common/heroBlock/HeroBlock";
import { Section } from "@/components/common/section/Section";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
//import { UserDetails } from "@/interfaces/userDetails";
import { NetworkError } from "@/interfaces/errors/networkError";
import { Contract, Invoice } from "@/interfaces/invoice";
import { StrapiImage } from "@/utils/strapi";
import { AccountMenu } from "./accountMenu/AccountMenu";
import { Billing } from "./billing/Billing";
import { AccountError } from "./accountError/AccountError";
import { UserInvalidate } from "./userInvalidate/UserInvalidate";
import styles from "./Account.module.scss";

export type InvoiceData = {
  contract: Contract | null;
  invoices: Invoice[];
};

export type BillingData = {
  invoicesWithContract: InvoiceData[];
  invoicesWithoutContract: Invoice[];
};

export type UnpaidInvoice = {
  invoiceId: string;
  taxeExcludedPrice: number;
  taxeIncludedPrice: number;
  balance: number;
  invoiceDate: string;
};

type MonthlyData = {
  yearMonth: string;
  invoiceData: BillingData;
};

type YearlyData = {
  year: number;
  unpaidData: UnpaidInvoice[];
};

type AccountProps = PageProps & {
  categoriesImages: {
    [name: string]: StrapiImage[] | undefined;
  };
};

export enum AccountTabType {
  LOCATIONS = "locations",
  BILLING = "billing",
  MY_ACCOUNT = "myAccount",
  DRIVERS = "drivers",
}

export const Account: NextPage<AccountProps> = ({
  layout,
  categoriesImages,
}: AccountProps) => {
  const intl = useIntl();

  const { keycloak, initialized } = useKeycloak();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUserError, setIsUserError] = useState<boolean>(false);
  const [isUserInvalidate, setIsUserInvalidate] = useState<boolean>(false);
  //const [userData, setUserData] = useState<UserDetails>();
  const [billingDataByMonth, setBillingDataByMonth] = useState<MonthlyData[]>(
    []
  );
  const [unpaidDataByYear, setUnpaidDataByYear] = useState<YearlyData[]>([]);
  const [invoiceData, setBillingData] = useState<BillingData>();
  const [unpaidData, setUnpaidData] = useState<UnpaidInvoice[]>();
  const [lastname, setLastname] = useState<string>("");
  const [firstname, setFirstname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [year, setYear] = useState<number>(dayjs().get("year"));
  const [month, setMonth] = useState<number>(dayjs().get("month"));
  const [selectedTab, setSelectedTab] = useState<AccountTabType>(
    AccountTabType.BILLING
  );

  const getUserInvoices = (
    startDate: string,
    endDate: string,
    token?: string
  ) =>
    new Promise<BillingData>((resolve) => {
      sendFetchRequest(
        `/customers/v1/invoices?StartDate=${startDate}&EndDate=${endDate}`,
        token
      )
        .then((json: BillingData) => {
          resolve(json);
        })
        .catch((e: NetworkError<null>) => {
          if (e.message === "204") {
            setIsUserInvalidate(true);
          } else {
            setIsUserError(true);
          }
        });
    });

  const getYearlyUnpaidTotals = (
    startDate: string,
    endDate: string,
    token?: string
  ) =>
    new Promise<UnpaidInvoice[]>((resolve) => {
      sendFetchRequest(
        `/customers/v1/getNotPaidInvoicesDetails?StartDate=${startDate}&EndDate=${endDate}`,
        token
      )
        .then((json: UnpaidInvoice[]) => resolve(json))
        .catch((e: NetworkError<null>) => {
          if (e.message === "204") {
            setIsUserInvalidate(true);
          } else {
            setIsUserError(true);
          }
        });
    });

  const getBillingDataByMonth = (findYM: string): BillingData | undefined => {
    return billingDataByMonth.find((idby) => idby.yearMonth === findYM)
      ?.invoiceData;
  };

  const getUnpaidDataByYear = (): UnpaidInvoice[] | undefined => {
    return unpaidDataByYear.find((udby) => udby.year === year)?.unpaidData;
  };

  const getYearMonth = (nextMonth = false): string => {
    if (nextMonth) {
      const nM = month === 11 ? 1 : month + 2;
      const nY = month === 11 ? year + 2 : year;

      return `${nY}-` + `0${nM}`.slice(-2);
    }

    return `${year}-` + `0${month + 1}`.slice(-2);
  };

  useEffect(() => {
    if (initialized && keycloak) {
      if (
        keycloak.tokenParsed &&
        typeof keycloak.tokenParsed.email === "string"
      ) {
        setLastname(keycloak.tokenParsed.family_name as unknown as string);
        setFirstname(keycloak.tokenParsed.given_name as unknown as string);
        setEmail(keycloak.tokenParsed.email as unknown as string);
      }
    }
  }, [initialized, keycloak]);

  useEffect(() => {
    if (initialized && keycloak) {
      if (
        keycloak.tokenParsed &&
        typeof keycloak.tokenParsed.email === "string"
      ) {
        setBillingData(getBillingDataByMonth(getYearMonth()));
        if (!invoiceData && !isLoading) {
          setIsLoading(true);
          getUserInvoices(
            `${getYearMonth()}-01`,
            `${getYearMonth(true)}-01`,
            keycloak.idToken
          )
            .then((value: BillingData) => {
              billingDataByMonth.push({
                yearMonth: getYearMonth(),
                invoiceData: value,
              });
              setBillingDataByMonth(billingDataByMonth);
              setBillingData(value);
              setIsLoading(false);
            })
            .catch((e) => {
              setIsLoading(false);
              console.log(e);
            });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, invoiceData, billingDataByMonth, keycloak, year, month]);

  useEffect(() => {
    if (initialized && keycloak) {
      if (
        keycloak.tokenParsed &&
        typeof keycloak.tokenParsed.email === "string"
      ) {
        const tmp = getUnpaidDataByYear();
        setUnpaidData(tmp);
        if (!tmp) {
          getYearlyUnpaidTotals(
            `${year}-01-01`,
            `${year}-12-31`,
            keycloak.idToken
          )
            .then((response) => {
              unpaidDataByYear.push({
                year: year,
                unpaidData: response,
              });
              setUnpaidDataByYear(unpaidDataByYear);
              setUnpaidData(response);
            })
            .catch((e) => console.log(e));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  return (
    <>
      {((!isUserError /*&& userData*/ && !isUserInvalidate) ||
        isUserError ||
        isUserInvalidate) && (
        <Layout
          layout={layout}
          defaultCanonicalRoute={routes.account}
          title={intl.formatMessage({ id: "myAccount" })}
          breadcrumbs={[
            { title: intl.formatMessage({ id: "home" }), url: "/" },
            {
              title: intl.formatMessage({ id: `myAccount` }),
              url: "/account/dashboard",
            },
          ]}
        >
          <HeroBlock
            title={intl.formatMessage({ id: "myAccount" })}
            hideSearchBlock
          />
          {!isUserError /*&& userData*/ && (
            <>
              <AccountMenu
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                lastname={lastname}
                firstname={firstname}
                isB2B={true}
              />
              <Section containerStyle={styles.section}>
                {selectedTab === AccountTabType.BILLING && (
                  <Billing
                    data={invoiceData}
                    unpaidData={unpaidData}
                    categoriesImages={categoriesImages}
                    year={year}
                    month={month}
                    setYear={setYear}
                    setMonth={setMonth}
                    isLoading={isLoading}
                  />
                )}
              </Section>
            </>
          )}
          {isUserError && (
            <AccountError lastname={lastname} firstname={firstname} />
          )}
          {isUserInvalidate && <UserInvalidate email={email} />}
        </Layout>
      )}
    </>
  );
};
